import { formatCLP } from '@/lib/format';
import {
  DetailIconButton,
  DetailInfoRows,
} from '@/shared/PanelPrimitives/PanelPrimitives';
import { LedgerRowSkeleton, PageHead } from '@/ui';
import {
  ArrowUpRightIcon,
  DeleteIcon,
  DownloadIcon,
  RefreshIcon,
  SendIcon,
  WalletIcon,
} from '@/ui/icons';
import PropTypes from 'prop-types';
import { useState } from 'react';
import OfferBreakdownList from '../solicitudes/OfferBreakdownList.jsx';
import './DiligenciaDetalleVista.css';

/**
 * Diligencia detail — Layout B (Datos + Panel). One `.page` document:
 * PageHead (kicker `Diligencia · rol` → display carátula → tribunal · corte
 * sub → meta band Actuación / Solicitada para / Comprometida / Encargado,
 * quiet icon actions) → `.dnav` band with the 5-stage stepper → `.cb-cols`
 * body: the diligencia's own data (Dirección · Detalle · Comuna · Notas) in a
 * sticky 322px aside, and ONE merged estado panel on the right — head (estado
 * title + sub + contextual action) · body (offer rows or note) · sticky
 * payment foot when there is money on the table.
 *
 * Purely presentational (props in), like CausaDetalleVista. The page
 * (VerDiligencia) derives `estado` from the diligenciaDisplay source of truth
 * and maps real quotes/movements onto these props; every action is a callback.
 */

// Default page size for the offers list; the rest fold behind "Ver todas".
const OFFER_PAGE = 5;

// Estados whose panel carries the sticky payment foot.
const FOOT_ESTADOS = [
  'listo',
  'reintentar',
  'procesando',
  'encurso',
  'finalizada',
];

// Estados that show the payment itself (amounts and comprobante), read from
// GET /payments. `listo` is not one: its amounts come from the chosen offer.
const PAYMENT_ESTADOS = ['reintentar', 'procesando', 'encurso', 'finalizada'];

const money = (v) => (v == null ? '—' : formatCLP(v));

// Icons come from the shared set (@/ui/icons). The 16-grid ones (Refresh /
// Send / Wallet / ArrowUpRight) carry the detail-head stroke math natively;
// the 24-grid Download / Delete take explicit size + stroke overrides so the
// rendered weight stays the same (1.4 on a 16 grid ≡ 2.1 on a 24 grid).

// Stepper: 5 stages; `current` is the active step index (0..4). `neverPaid`
// (expirada/cancelada) means the diligencia landed on the terminal step without
// ever passing through the earlier stages — so they render as never-reached
// (plain number, no ✓), not as falsely "done". The terminal step says HOW it
// ended (owner naming): Finalizada by default, Cancelada/Expirada when so.
const STEPS = ['Borrador', 'Cotizando', 'Por pagar', 'En curso'];
function Stepper({ current, neverPaid, closedLabel = 'Finalizada' }) {
  const steps = [...STEPS, closedLabel];
  return (
    <div className="stepper2">
      {steps.map((label, i) => {
        const done = i < current && !neverPaid;
        const now = i === current;
        return (
          <div
            key={label}
            className={'stp' + (done ? ' stp--done' : now ? ' stp--now' : '')}
          >
            <span className="stp__dot">{done ? '✓' : i + 1}</span>
            <span className="stp__lbl">{label}</span>
          </div>
        );
      })}
    </div>
  );
}
Stepper.propTypes = {
  current: PropTypes.number,
  neverPaid: PropTypes.bool,
  closedLabel: PropTypes.string,
};

// Left aside, ALL estados: only the diligencia's own task fields — identity
// (rol/tribunal) lives in the kicker/sub and Actuación/plazo/Encargado in the
// meta band. Empty values render "—", never hidden. While borrador, "Editar"
// opens NuevaDiligenciaModal (owner decision: no inline form; the aside is
// read-only rows in every state).
function DatosAside({ draft = {}, onEditar }) {
  const rows = [
    ['Dirección', draft.address],
    ['Detalle', draft.detalle],
    ['Comuna', draft.comuna],
    ['Notas', draft.notas],
  ];
  return (
    <aside className="cb-aside">
      <div className="cb-fichahead">
        <span className="kicker">Datos de la diligencia</span>
        {onEditar && (
          <button type="button" className="card__link" onClick={onEditar}>
            Editar
          </button>
        )}
      </div>
      <DetailInfoRows rows={rows} />
    </aside>
  );
}
DatosAside.propTypes = {
  draft: PropTypes.object,
  onEditar: PropTypes.func,
};

// One offer row inside the panel (hairline-separated, no card double border).
// Picker mode adds the radio; a chosen row shows its tag (Adjudicada / Sin
// pagar) instead of the quote-status pill; closed rows render muted.
function OfferRow({ offer, picker, selected, onSelect }) {
  const on = picker ? selected : offer.chosen;
  const clickable = picker && offer.payable;
  const cls =
    'cb-offer' +
    (on ? ' cb-offer--on' : '') +
    (offer.closed ? ' cb-offer--mute' : '');
  const showStatus = offer.label && !(offer.chosen && offer.chosenTag);
  return (
    <div
      className={cls}
      onClick={clickable ? () => onSelect(offer.id) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {picker && offer.payable && (
        <span
          className={'offer__radio' + (selected ? ' offer__radio--on' : '')}
        />
      )}
      <div className="cb-offer__main">
        <div className="cb-offer__top">
          <span className="cb-offer__name">{offer.name}</span>
          {showStatus && (
            <span className={`st st--${offer.tone || 'info'}`}>
              <span className="st__dot" />
              {offer.label}
            </span>
          )}
          {offer.recommended && (
            <span className="tag tag--ok">Recomendada</span>
          )}
          {offer.chosen && offer.chosenTag && (
            <span className={`tag tag--${offer.chosenTone || 'ok'}`}>
              {offer.chosenTag}
            </span>
          )}
        </div>
        {offer.sub && <div className="cb-offer__sub">{offer.sub}</div>}
        <OfferBreakdownList
          lines={offer.breakdown}
          className="cb-offer__breakdown"
        />
      </div>
      <span className="cb-offer__amt">
        {offer.price > 0 ? formatCLP(offer.price) : '—'}
      </span>
    </div>
  );
}
OfferRow.propTypes = {
  offer: PropTypes.object.isRequired,
  picker: PropTypes.bool,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
};

// Offer rows with the "Ver todas · N" fold past OFFER_PAGE. In picker mode
// the payable (accepted) offers sort first.
function QuotesFold({ rows, picker, selectedOfferId, onSelectOffer }) {
  const [showAll, setShowAll] = useState(false);
  const ordered = picker
    ? [...rows].sort((a, b) => (b.payable ? 1 : 0) - (a.payable ? 1 : 0))
    : rows;
  const shown = showAll ? ordered : ordered.slice(0, OFFER_PAGE);
  return (
    <>
      {shown.map((o) => (
        <OfferRow
          key={o.id}
          offer={o}
          picker={picker}
          selected={picker && selectedOfferId === o.id}
          onSelect={onSelectOffer}
        />
      ))}
      {ordered.length > OFFER_PAGE && (
        <button
          type="button"
          className="cb-panel__foldbtn"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? 'Ver menos' : `Ver todas · ${ordered.length}`}
        </button>
      )}
    </>
  );
}
QuotesFold.propTypes = {
  rows: PropTypes.array.isRequired,
  picker: PropTypes.bool,
  selectedOfferId: PropTypes.string,
  onSelectOffer: PropTypes.func,
};

// Panel head line per estado: title + sub. Copy uses only real props —
// counts, plazo, offers — never invented data.
function panelStatus({
  estado,
  draftExpired,
  offersCount,
  deadlineText,
  hasOffers,
  payableCount,
  paymentLink,
}) {
  switch (estado) {
    case 'borrador':
      return draftExpired
        ? {
            title: 'Expiró sin enviar',
            sub: 'El plazo venció antes de enviarla a cotizar.',
          }
        : {
            title: 'Borrador',
            sub: 'No enviada a cotizar. Los datos aún se pueden editar.',
          };
    case 'cotizando':
      return {
        title: 'Esperando ofertas',
        sub: offersCount
          ? [
              `${offersCount} ${
                offersCount === 1 ? 'solicitud enviada' : 'solicitudes enviadas'
              }`,
              deadlineText ? `cierra el ${deadlineText}` : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined,
      };
    case 'sinofertas':
      return {
        title: 'Sin ofertas',
        sub: 'Ningún receptor ofertó dentro del plazo.',
      };
    case 'listo':
      return {
        title: 'Elige una oferta para pagar',
        sub: payableCount
          ? `${
              payableCount === 1
                ? '1 receptor ofertó'
                : `${payableCount} receptores ofertaron`
            } dentro del plazo.`
          : undefined,
      };
    case 'reintentar':
      return {
        title: 'Reintentar pago',
        sub: 'El cobro anterior no se completó.',
      };
    // PAYMENT_PROCESSING means the checkout link was handed out and no webhook
    // has arrived — nothing more. An abandoned checkout lands here too, and the
    // "no necesita hacer nada" copy left that payer with no way back to the
    // link the charge is waiting on. Toku owns the invoice and admits a single
    // payment on it, so reopening the same link is safe even when the payment
    // already went through and the webhook is only late.
    case 'procesando':
      return {
        title: 'Confirmando el cobro',
        sub: paymentLink
          ? 'En proceso con la Pasarela de pago. Si dejaste el pago a medias, retómalo con el mismo link.'
          : 'En proceso con la Pasarela de pago. No necesitas hacer nada.',
      };
    case 'encurso':
      return { title: 'En curso con el receptor' };
    case 'finalizada':
      return { title: 'Pagada · cumplida', sub: 'La actuación fue cumplida.' };
    case 'expirada':
      return {
        title: 'Expirada',
        sub: hasOffers
          ? 'Se recibieron ofertas, pero ninguna se pagó dentro del plazo.'
          : 'Ningún receptor ofertó dentro del plazo.',
      };
    case 'cancelada':
      return {
        title: 'Cancelada',
        sub: 'Se canceló antes de pagar. No se cobró nada.',
      };
    default:
      return { title: estado };
  }
}

// Contextual action in the panel head. Paid states show the "Pagada" pill;
// the pre-send / re-send estados carry their primary CTA.
function PanelHeadAction({
  estado,
  draftExpired,
  busy,
  onEnviar,
  onRecotizar,
  onNueva,
}) {
  if (estado === 'borrador') {
    if (draftExpired) {
      // "Volver a cotizar" on a draft edits in place: the data never went out,
      // so the API still accepts edit + send. No cancel/re-create needed.
      return onRecotizar ? (
        <button
          type="button"
          className="t-btn t-btn--primary"
          onClick={onRecotizar}
          disabled={busy}
        >
          <RefreshIcon />
          Volver a cotizar
        </button>
      ) : null;
    }
    return (
      <button
        type="button"
        className="t-btn t-btn--primary"
        onClick={onEnviar}
        disabled={busy}
      >
        <SendIcon />
        Solicitar cotizaciones
      </button>
    );
  }
  if (estado === 'sinofertas' && onRecotizar) {
    return (
      <button
        type="button"
        className="t-btn t-btn--primary"
        onClick={onRecotizar}
        disabled={busy}
      >
        <RefreshIcon />
        Recotizar
      </button>
    );
  }
  // Expirada: no resend — once it went out to quote, its data is fixed and the
  // API 409s any edit/resend (task.service.ts updateDraftTask/sendDiligencia
  // require DRAFT; see lib/diligenciaDisplay.js EXPIRED). The primary path is
  // "Volver a cotizar": close it (the cancel transition frees the causa) +
  // re-create prefilled; the close-only variant lives on the head icon as
  // "Archivar" — never "Cancelar", which is reserved for non-expired.
  // Finalizada: terminal, so the causa is already free — the head action
  // re-encarga a new prefilled diligencia in the same causa (via the goNueva
  // preload). The paid signal stays on the title/body/foot.
  if ((estado === 'expirada' || estado === 'finalizada') && onNueva) {
    return (
      <button
        type="button"
        className="t-btn t-btn--primary"
        onClick={onNueva}
        disabled={busy}
      >
        <RefreshIcon />
        Volver a cotizar
      </button>
    );
  }
  if (estado === 'encurso' || estado === 'finalizada') {
    return (
      <span className="st st--ok">
        <span className="st__dot" />
        Pagada
      </span>
    );
  }
  return null;
}
PanelHeadAction.propTypes = {
  estado: PropTypes.string.isRequired,
  draftExpired: PropTypes.bool,
  busy: PropTypes.bool,
  onEnviar: PropTypes.func,
  onRecotizar: PropTypes.func,
  onNueva: PropTypes.func,
};

// Panel body per estado: offer rows and/or a short note. The paid phases show
// the adjudicated row + a status line (late overlays the warn, as before).
function PanelBody({
  estado,
  draftExpired,
  late,
  alertText,
  offers,
  offersFailed,
  picker,
  selectedOfferId,
  onSelectOffer,
  chosen,
  onCancelar,
}) {
  if (estado === 'borrador') {
    return (
      <p className="cb-panel__note">
        {draftExpired
          ? 'No se envió a los receptores ni se cobró nada. Vuelve a cotizarla con un nuevo plazo, o archívala para liberar la causa.'
          : 'Al enviar, los receptores de la corte reciben la solicitud y pueden ofertar honorarios y fecha. Es irreversible: los datos quedan fijos.'}
      </p>
    );
  }

  if (estado === 'sinofertas') {
    return (
      <p className="cb-panel__note">
        Recotiza para volver a enviar la solicitud a los receptores de la corte.
      </p>
    );
  }

  if (estado === 'cotizando') {
    return (
      <>
        {offers.length > 0 && <QuotesFold rows={offers} />}
        <p
          className={
            'cb-panel__note' +
            (offers.length > 0 ? ' cb-panel__note--after' : '')
          }
        >
          {offersFailed
            ? 'No se pudieron cargar las ofertas. Puede haber ofertas que esta pantalla no está mostrando.'
            : offers.length > 0
              ? 'Cuando cierre el plazo o llegue una oferta conveniente, podrás elegir y pagar aquí.'
              : 'La solicitud llegó a los receptores de la corte. Las ofertas aparecerán aquí.'}
        </p>
      </>
    );
  }

  if (estado === 'listo') {
    return (
      <>
        <QuotesFold
          rows={offers}
          picker={picker}
          selectedOfferId={selectedOfferId}
          onSelectOffer={onSelectOffer}
        />
        <p className="cb-panel__note cb-panel__note--after">
          Pagar una oferta descarta las demás. Recepthor cobra el total al
          estudio y le transfiere los honorarios al receptor.
        </p>
      </>
    );
  }

  // The row the money talks about: the adjudicated offer, or (reintentar /
  // no movement hydrated yet) the `chosen` prop as a synthesized row.
  const chosenRows = offers.filter((o) => o.chosen);
  const fallbackRow = chosen
    ? [
        {
          id: '_chosen',
          name: chosen.name,
          price: chosen.price,
          chosen: true,
          chosenTag: estado === 'cancelada' ? 'Sin pagar' : 'Adjudicada',
          chosenTone: estado === 'cancelada' ? 'err' : 'ok',
        },
      ]
    : [];

  if (estado === 'reintentar') {
    const rows = chosenRows.length ? chosenRows : fallbackRow;
    return (
      <>
        {rows.map((o) => (
          <OfferRow key={o.id} offer={o} />
        ))}
        <p
          className={
            'cb-panel__note' + (rows.length ? ' cb-panel__note--after' : '')
          }
        >
          {alertText || 'El cobro no se completó. No se descontó dinero.'}
        </p>
      </>
    );
  }

  if (['procesando', 'encurso', 'finalizada'].includes(estado)) {
    const rows = chosenRows.length ? chosenRows : fallbackRow;
    const procesando = estado === 'procesando';
    const finalizada = estado === 'finalizada';
    const tone = procesando || late ? 'warn' : finalizada ? 'ok' : 'info';
    const label = procesando
      ? 'Cobro en proceso'
      : late
        ? 'Pasó la fecha comprometida'
        : finalizada
          ? 'Actuación cumplida'
          : 'En curso con el receptor';
    return (
      <>
        {rows.map((o) => (
          <OfferRow key={o.id} offer={o} />
        ))}
        <div className="cb-statusline">
          <span className={`st st--${tone}`}>
            <span className="st__dot" />
            {label}
          </span>
        </div>
        {alertText && (
          <p className="cb-panel__note cb-panel__note--after">{alertText}</p>
        )}
      </>
    );
  }

  if (estado === 'expirada') {
    return (
      <>
        {offers.length > 0 && <QuotesFold rows={offers} />}
        <p
          className={
            'cb-panel__note' +
            (offers.length > 0 ? ' cb-panel__note--after' : '')
          }
        >
          {onCancelar
            ? 'No se cobró nada. Vuelve a cotizar para abrirla de nuevo, o archívala para solo liberar la causa.'
            : 'No se cobró nada.'}
        </p>
      </>
    );
  }

  // cancelada — the chosen offer (if one existed) stayed unpaid.
  const rows = chosenRows.length ? chosenRows : fallbackRow;
  return (
    <>
      {rows.map((o) => (
        <OfferRow key={o.id} offer={o} />
      ))}
      <p
        className={
          'cb-panel__note' + (rows.length ? ' cb-panel__note--after' : '')
        }
      >
        La causa quedó libre para volver a cotizar.
      </p>
    </>
  );
}
PanelBody.propTypes = {
  estado: PropTypes.string.isRequired,
  offersFailed: PropTypes.bool,
  draftExpired: PropTypes.bool,
  late: PropTypes.bool,
  alertText: PropTypes.string,
  offers: PropTypes.array,
  picker: PropTypes.bool,
  selectedOfferId: PropTypes.string,
  onSelectOffer: PropTypes.func,
  chosen: PropTypes.object,
  onCancelar: PropTypes.func,
};

// Sticky payment foot: total + honorarios/fee/IVA break (prebuilt in `pay` —
// IVA is on the fee, never recomputed here) + the money action per estado.
function PanelFoot({
  estado,
  chosen,
  pay,
  payError,
  paymentsAvailable = true,
  busy,
  onPay,
  onComprobante,
  comprobanteGenerando = false,
  paymentLink,
  retryable,
}) {
  if (!FOOT_ESTADOS.includes(estado)) return null;
  // Without payment:read the payment is not read: no amounts, no comprobante
  // and no read error, just who can see it.
  const paymentHidden = !paymentsAvailable && PAYMENT_ESTADOS.includes(estado);
  const paid = ['encurso', 'finalizada'].includes(estado);
  const base =
    estado === 'procesando' ? 'Total' : paid ? 'Total pagado' : 'Total a pagar';
  // With no payment read there is no total to label: just "Pago".
  const lblBase = paymentHidden ? 'Pago' : base;
  const lbl = chosen?.name ? `${lblBase} · ${chosen.name}` : lblBase;
  return (
    <div className="cb-panel__foot">
      <div className="cb-pay">
        <div className="cb-pay__l">
          <span className="cb-pay__lbl">{lbl}</span>
          {/* A failed read of the payment renders here and not as a dash: the
              amounts show a dash when there is no payment too, so without this
              the two states are the same picture. */}
          {paymentHidden ? (
            <span className="cb-pay__break">
              El detalle del pago lo ve un administrador del estudio.
            </span>
          ) : payError ? (
            <>
              <span className="cb-pay__amt cb-pay__amt--err">
                No se pudo cargar el pago
              </span>
              <span className="cb-pay__break">
                Los montos y el comprobante no son legibles hasta que la lectura
                funcione.{' '}
                <button
                  type="button"
                  className="cb-pay__retry"
                  onClick={payError.onRetry}
                  disabled={payError.busy}
                >
                  {payError.busy ? 'Reintentando…' : 'Reintentar'}
                </button>
              </span>
            </>
          ) : (
            <>
              <span className="cb-pay__amt">{money(pay.total)}</span>
              {/* A paid diligencia carries the frozen serviceFee (IVA included,
                  no net/IVA split), so the break line shows one component. */}
              {!pay.hideBreakdown && (
                <span className="cb-pay__break">
                  {pay.legacyFee != null ? (
                    <>
                      honorarios {money(pay.honorarios)} + fee e IVA{' '}
                      {money(pay.legacyFee)}
                    </>
                  ) : pay.serviceFee != null ? (
                    <>
                      honorarios {money(pay.honorarios)} + fee (IVA incl.){' '}
                      {money(pay.serviceFee)}
                    </>
                  ) : (
                    <>
                      honorarios {money(pay.honorarios)} + fee {money(pay.fee)}{' '}
                      + IVA {money(pay.iva)}
                    </>
                  )}
                </span>
              )}
            </>
          )}
        </div>
        {estado === 'listo' && (
          <button
            type="button"
            className="t-btn t-btn--primary"
            onClick={onPay}
            disabled={busy || !chosen}
          >
            <WalletIcon />
            Pagar
          </button>
        )}
        {/* Retry reopens the checkout on the committed offer, and that
            checkout carries the redirection back to the application. The saved
            link does not: it is the raw one the live payin froze. So the button
            wins whenever it can arm, and the link only covers the case where
            the committed offer could not be read and the button would sit
            dead. It arms on the quote row, not on `chosen`: `chosen` falls back
            to the movement when the quotes read fails, and the checkout needs
            the quote. */}
        {estado === 'reintentar' &&
          (retryable || !paymentLink ? (
            <button
              type="button"
              className="t-btn t-btn--primary"
              onClick={onPay}
              disabled={busy || !retryable}
            >
              <WalletIcon />
              Reintentar
            </button>
          ) : (
            <a className="t-btn t-btn--primary" href={paymentLink}>
              <WalletIcon />
              Retomar el pago
            </a>
          ))}
        {/* The saved link of the live attempt, straight from the task read: the
            payer returns to the Pasarela without emitting a second invoice. It
            is the raw link, so Toku does NOT redirect back to the diligencia —
            the decorated one only exists for the checkout that created it. Not
            a button: it leaves the app. */}
        {estado === 'procesando' &&
          (paymentLink ? (
            <a className="t-btn t-btn--primary" href={paymentLink}>
              <WalletIcon />
              Retomar el pago
            </a>
          ) : (
            <span className="st st--warn">
              <span className="st__dot" />
              En proceso
            </span>
          ))}
        {paid && !paymentHidden && onComprobante && (
          <button
            type="button"
            className="t-btn t-btn--quiet"
            onClick={onComprobante}
          >
            <DownloadIcon size={15} strokeWidth={2.1} />
            Comprobante
          </button>
        )}
        {/* Handler presence = receipt availability: no handler on a paid
            estado means the comprobante isn't archived yet. While the page
            is still re-reading for it, the button says so; once it stops, it
            goes back to "no disponible". */}
        {paid && !paymentHidden && !onComprobante && (
          <button
            type="button"
            className="t-btn t-btn--quiet"
            disabled
            title={
              comprobanteGenerando
                ? 'Generando comprobante'
                : 'Comprobante no disponible'
            }
            aria-label={
              comprobanteGenerando
                ? 'Generando comprobante'
                : 'Comprobante no disponible'
            }
          >
            <DownloadIcon size={15} strokeWidth={2.1} />
            {comprobanteGenerando
              ? 'Generando comprobante'
              : 'Comprobante no disponible'}
          </button>
        )}
      </div>
    </div>
  );
}
PanelFoot.propTypes = {
  estado: PropTypes.string.isRequired,
  chosen: PropTypes.object,
  pay: PropTypes.object,
  payError: PropTypes.shape({
    busy: PropTypes.bool,
    onRetry: PropTypes.func,
  }),
  paymentsAvailable: PropTypes.bool,
  busy: PropTypes.bool,
  onPay: PropTypes.func,
  onComprobante: PropTypes.func,
  comprobanteGenerando: PropTypes.bool,
  paymentLink: PropTypes.string,
  retryable: PropTypes.bool,
};

// While the first read of the diligencia and its causa is in flight: a
// skeleton instead of the view with every field as "—".
export function DiligenciaDetalleCargando() {
  return (
    <main
      className="page dd-page reveal"
      aria-busy="true"
      aria-label="Cargando diligencia"
    >
      <div className="ledger-body">
        {[...Array(5)].map((_, i) => (
          <LedgerRowSkeleton key={i} />
        ))}
      </div>
    </main>
  );
}

export function DiligenciaDetalle({
  offersFailed,
  payError,
  paymentsAvailable = true,
  caratula,
  rol,
  tribunalName,
  encargado,
  actuacion,
  deadlineText,
  offeredDateText,
  estado,
  draftExpired,
  currentStep,
  neverPaid,
  late,
  alertText,
  offers = [],
  offersCount,
  courtName,
  selectedOfferId,
  onSelectOffer,
  chosen,
  pay = {},
  draft = {},
  busy,
  onPay,
  onEnviar,
  onRecotizar,
  onEditar,
  onCancelar,
  onNueva,
  onVerCausa,
  onComprobante,
  comprobanteGenerando,
  paymentLink,
}) {
  const picker = estado === 'listo';
  const sub = [tribunalName, courtName].filter(Boolean).join(' · ');
  const verCausaLabel = rol ? `Ver causa ${rol}` : 'Ver causa';
  const actions =
    onVerCausa || onCancelar ? (
      <>
        {onVerCausa && (
          <DetailIconButton title={verCausaLabel} onClick={onVerCausa}>
            <ArrowUpRightIcon />
          </DetailIconButton>
        )}
        {onCancelar && (
          <DetailIconButton
            // Vocabulary: an expired diligencia "archives"
            // (close + free the causa, nothing was charged); "Cancelar" is
            // the manual abandonment of a live one.
            title={
              draftExpired || estado === 'expirada' ? 'Archivar' : 'Cancelar'
            }
            onClick={onCancelar}
          >
            <DeleteIcon size={15} style={{ strokeWidth: 2.1 }} />
          </DetailIconButton>
        )}
      </>
    ) : undefined;

  const status = panelStatus({
    estado,
    draftExpired,
    offersCount: offersCount ?? offers.length,
    deadlineText,
    hasOffers: offers.length > 0,
    payableCount: offers.filter((o) => o.payable).length,
    paymentLink,
  });

  return (
    <main className="page dd-page reveal">
      <PageHead
        kicker={rol ? `Diligencia · ${rol}` : 'Diligencia'}
        title={caratula || 'Diligencia'}
        sub={sub || undefined}
        action={actions}
        meta={[
          { k: 'Actuación', v: actuacion },
          { k: 'Solicitada para', v: deadlineText },
          { k: 'Comprometida', v: offeredDateText },
          { k: 'Encargado', v: encargado },
        ]}
      />
      <div className="dnav">
        <Stepper
          current={currentStep}
          neverPaid={neverPaid}
          closedLabel={
            estado === 'cancelada'
              ? 'Cancelada'
              : estado === 'expirada'
                ? 'Expirada'
                : 'Finalizada'
          }
        />
      </div>
      <div className="dbody cb-cols">
        <DatosAside
          draft={draft}
          onEditar={estado === 'borrador' ? onEditar : undefined}
        />
        <div>
          <div className="kicker dil-side__kicker">Estado</div>
          <div className="cb-panel">
            <div className="cb-panel__head">
              <div className="cb-panel__hl">
                <span className="cb-panel__title">{status.title}</span>
                {status.sub && (
                  <span className="cb-panel__sub">{status.sub}</span>
                )}
              </div>
              <PanelHeadAction
                estado={estado}
                draftExpired={draftExpired}
                busy={busy}
                onEnviar={onEnviar}
                onRecotizar={onRecotizar}
                onNueva={onNueva}
              />
            </div>
            <div className="cb-panel__body">
              <PanelBody
                estado={estado}
                draftExpired={draftExpired}
                late={late}
                alertText={alertText}
                offers={offers}
                offersFailed={offersFailed}
                picker={picker}
                selectedOfferId={selectedOfferId}
                onSelectOffer={onSelectOffer}
                chosen={chosen}
                onCancelar={onCancelar}
              />
            </div>
            <PanelFoot
              estado={estado}
              chosen={chosen}
              pay={pay}
              payError={payError}
              paymentsAvailable={paymentsAvailable}
              busy={busy}
              onPay={onPay}
              onComprobante={onComprobante}
              comprobanteGenerando={comprobanteGenerando}
              paymentLink={paymentLink}
              retryable={offers.some((o) => o.chosen)}
            />
          </div>
        </div>
      </div>
    </main>
  );
}

DiligenciaDetalle.propTypes = {
  offersFailed: PropTypes.bool,
  payError: PropTypes.shape({
    busy: PropTypes.bool,
    onRetry: PropTypes.func,
  }),
  paymentsAvailable: PropTypes.bool,
  caratula: PropTypes.string,
  rol: PropTypes.string,
  tribunalName: PropTypes.string,
  encargado: PropTypes.string,
  actuacion: PropTypes.string,
  deadlineText: PropTypes.string,
  offeredDateText: PropTypes.string,
  estado: PropTypes.string.isRequired,
  draftExpired: PropTypes.bool,
  currentStep: PropTypes.number,
  neverPaid: PropTypes.bool,
  late: PropTypes.bool,
  alertText: PropTypes.string,
  offers: PropTypes.array,
  offersCount: PropTypes.number,
  courtName: PropTypes.string,
  selectedOfferId: PropTypes.string,
  onSelectOffer: PropTypes.func,
  chosen: PropTypes.object,
  pay: PropTypes.object,
  draft: PropTypes.object,
  busy: PropTypes.bool,
  onPay: PropTypes.func,
  onEnviar: PropTypes.func,
  onRecotizar: PropTypes.func,
  onEditar: PropTypes.func,
  onCancelar: PropTypes.func,
  onNueva: PropTypes.func,
  onVerCausa: PropTypes.func,
  onComprobante: PropTypes.func,
  comprobanteGenerando: PropTypes.bool,
  paymentLink: PropTypes.string,
};

export default DiligenciaDetalle;
